<script setup>
  import { ref, computed, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import Icon from '@/components/icons/Icon.vue';
  import CopyContent from '@/components/CopyContent.vue';
  import ScrollAffordance from '@/components/ScrollAffordance.vue';
  import SegmentedControl from '@/components/controls/SegmentedControl.vue';
  import SegmentedControlButton from '@/components/controls/SegmentedControlButton.vue';
  import { useWidgetStore } from '@/stores/WidgetStore';
  import { useUserStore } from '@/stores/UserStore';
  import { useSpotStore } from '@/stores/SpotStore';
  import { useGoogleAnalyticsStore } from '@/stores/GoogleAnalyticsStore';
  import { useToaster } from '@/composables/useToaster';

  const widgetStore = useWidgetStore();
  const userStore = useUserStore();
  const spotStore = useSpotStore();
  const googleAnalyticsStore = useGoogleAnalyticsStore();

  const MAP_TYPES = ['contrast', 'map', 'satellite', 'hybrid', 'terrain'];
  const HEX_COLOR = /^#?([0-9a-f]{6})$/i;
  const SPEED_UNITS = { mph: 'mph', kph: 'kph', kts: 'kts', mps: 'm/s' };
  const TEMP_UNITS = { f: '°F', c: '°C' };

  const toaster = useToaster();
  const { t } = useI18n();

  // Text fields edit drafts, committed on change so the preview doesn't reload per keystroke
  const colorDraft = ref('');
  const widthDraft = ref(0);
  const heightDraft = ref(0);

  const widgetId = computed(() => widgetStore.activeWidgetId);
  const definition = computed(() => widgetStore.getDefinition(widgetId.value));
  const settings = computed(() => widgetStore.settings[widgetId.value]);
  const embed = computed(() => widgetStore.embeds[widgetId.value]);
  const idPrefix = computed(() => `widget-editor-${widgetId.value}`);
  const hasMapType = computed(() => settings.value.mapType !== undefined);
  const hasProfile = computed(() => settings.value.profileId !== undefined);
  const profiles = computed(() => userStore.userProfiles?.profiles ?? []);

  // Map widgets have their own title; the others show a name, the page's spot name by default
  const titleField = computed(() => definition.value.titleSetting);
  const titlePlaceholder = computed(() => widgetStore.defaults.spotName || spotStore.spot?.name || '');

  const title = computed({
    get: () => settings.value[titleField.value],
    set: (value) => { settings.value[titleField.value] = value; }
  });

  const color = computed(() => settings.value.color || widgetStore.defaults.color);
  const presetColors = computed(() => widgetStore.presetColors);

  const customColors = computed(() => widgetStore.customColors
    .filter(custom => !presetColors.value.some(preset => preset.color === custom)));

  // The widget's own units, else the Global Settings'; picking one makes it the widget's own
  const unitsWind = computed({
    get: () => settings.value.unitsWind || widgetStore.defaults.unitsWind,
    set: (value) => { settings.value.unitsWind = value; }
  });

  const unitsTemp = computed({
    get: () => settings.value.unitsTemp || widgetStore.defaults.unitsTemp,
    set: (value) => { settings.value.unitsTemp = value; }
  });

  const sizeLimits = computed(() => definition.value.sizeLimits);

  // A Web_Widget_* analytics event for the widget being edited
  function sendWidgetEvent(name, parameters = {}) {
    googleAnalyticsStore.sendEvent(name, {
      Spot_Name: widgetStore.analyticsSpotName,
      Widget: definition.value.analyticsName,
      ...parameters
    });
  }

  function isSelectedColor(swatch) {
    return swatch.toLowerCase() === color.value.toLowerCase();
  }

  function setColor(value, { remember = false } = {}) {
    const match = HEX_COLOR.exec(value.trim());

    if (!match) {
      colorDraft.value = color.value;
      return;
    }

    const hex = `#${match[1].toLowerCase()}`;

    if (hex !== color.value.toLowerCase()) sendWidgetEvent('Web_Widget_Color_Changed');

    settings.value.color = hex;
    colorDraft.value = hex;

    if (remember) widgetStore.addCustomColor(hex);
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(Math.round(value), min), max);
  }

  function handleMapTypeSelect(mapType) {
    settings.value.mapType = mapType;
  }

  // Legacy setWindMapProfile(): a profile replaces the spot, so the saved view goes too
  function handleProfileChange() {
    settings.value.center = '';
    settings.value.zoom = '';
  }

  function handleColorPicked(event) {
    setColor(event.target.value, { remember: true });
  }

  function handleColorTyped() {
    setColor(colorDraft.value, { remember: true });
  }

  function handleSwatchSelect(swatch) {
    setColor(swatch);
  }

  // segmentChanged only comes from the user picking a unit, never from a write-back
  function handleUnitsWindChange({ value }) {
    sendWidgetEvent('Web_Widget_Units_Changed', { Unit: `Speed-${value}` });
  }

  function handleUnitsTempChange({ value }) {
    sendWidgetEvent('Web_Widget_Units_Changed', { Unit: `Temperature-${value}` });
  }

  // On change, once the title is committed, not on every keystroke
  function handleTitleChange() {
    sendWidgetEvent('Web_Widget_Title_Change');
  }

  function handleWidthChange() {
    const { minWidth, maxWidth } = sizeLimits.value;
    const width = Number.isFinite(widthDraft.value) ? clamp(widthDraft.value, minWidth, maxWidth) : settings.value.width;

    settings.value.width = width;
    widthDraft.value = width;
  }

  function handleHeightChange() {
    const { minHeight, maxHeight } = sizeLimits.value;
    const height = Number.isFinite(heightDraft.value) ? clamp(heightDraft.value, minHeight, maxHeight) : settings.value.height;

    settings.value.height = height;
    heightDraft.value = height;
  }

  function handleReset() {
    widgetStore.resetWidget(widgetId.value);
  }

  function handleCopied() {
    sendWidgetEvent('Web_Widget_Embed_Copied', { Location: 'Customizer' });

    toaster.toast({
      type: 'success',
      content: t('widgets.codeCopied')
    });
  }

  // Keep the drafts in step with the widget's settings (another widget, a reset, a swatch)
  watch([color, () => settings.value.width, () => settings.value.height], ([newColor, width, height]) => {
    colorDraft.value = newColor;
    widthDraft.value = width;
    heightDraft.value = height;
  }, { immediate: true });
</script>

<template>
  <section class="jwx-widget-editor__settings jwx-widget-editor-settings" :aria-label="t('widgets.editorSettings')">
    <div class="jwx-widget-editor-settings__options">
      <div class="jwx-widget-editor-settings__column">
        <div v-if="titleField || hasProfile" class="jwx-widget-editor-settings__fields">
          <div v-if="titleField" class="jwx-widget-editor-settings__field">
            <label :for="`${idPrefix}-title`" class="jwx-widget-editor-settings__label">{{ t('widgets.settings.widgetTitle') }}</label>
            <input
              :id="`${idPrefix}-title`"
              v-model.trim="title"
              @change="handleTitleChange"
              type="text"
              class="jwx-widget-editor-settings__input"
              :placeholder="titlePlaceholder"
            >
          </div>
          <div v-if="hasProfile" class="jwx-widget-editor-settings__field">
            <label :for="`${idPrefix}-profile`" class="jwx-widget-editor-settings__label">{{ t('widgets.settings.profileSpots') }}</label>
            <div class="jwx-select-control" data-border="false">
              <select :id="`${idPrefix}-profile`" v-model="settings.profileId" @change="handleProfileChange">
                <option value="">{{ t('common.none') }}</option>
                <option v-for="profile of profiles" :key="profile.profile_id" :value="profile.profile_id">{{ profile.name }}</option>
              </select>
              <Icon name="chevron-up-down" class="jwx-select-control__action-icon" />
            </div>
          </div>
        </div>

        <div v-if="hasMapType" class="jwx-widget-editor-settings__group" role="group" :aria-labelledby="`${idPrefix}-map-type`">
          <h3 :id="`${idPrefix}-map-type`" class="jwx-widget-editor-settings__heading">{{ t('widgets.settings.mapType') }}</h3>
          <ScrollAffordance
            v-slot="{ scrollerRef, fadeEdges }"
            :buttons="false"
            :previousLabel="t('widgets.settings.mapType')"
            :nextLabel="t('widgets.settings.mapType')"
          >
            <div :ref="scrollerRef" class="jwx-widget-editor-settings__chips" :data-fade="fadeEdges">
              <button
                v-for="mapType of MAP_TYPES"
                :key="mapType"
                @click="handleMapTypeSelect(mapType)"
                type="button"
                class="jwx-button-control jwx-widget-editor-settings__chip"
                data-shape="pill"
                :data-selected="settings.mapType === mapType"
                :aria-pressed="settings.mapType === mapType"
              >
                {{ t(`widgets.mapTypes.${mapType}`) }}
              </button>
            </div>
          </ScrollAffordance>
        </div>

        <div class="jwx-widget-editor-settings__group" role="group" :aria-labelledby="`${idPrefix}-color`">
          <h3 :id="`${idPrefix}-color`" class="jwx-widget-editor-settings__heading">{{ t('widgets.settings.color') }}</h3>
          <div class="jwx-widget-editor-settings__color">
            <input
              :id="`${idPrefix}-color-picker`"
              @change="handleColorPicked"
              :value="color"
              type="color"
              class="jwx-widget-editor-settings__color-picker"
            >
            <label
              :for="`${idPrefix}-color-picker`"
              class="jwx-widget-editor-settings__color-swatch"
              :style="{ '--jwx-widget-editor-settings-color': color }"
            >
              <span class="sr-only">{{ t('widgets.settings.chooseColor') }}</span>
            </label>
            <input
              v-model="colorDraft"
              @change="handleColorTyped"
              type="text"
              class="jwx-widget-editor-settings__input"
              spellcheck="false"
              maxlength="7"
              :aria-label="t('widgets.settings.colorHex')"
            >
          </div>
          <ScrollAffordance
            v-slot="{ scrollerRef, fadeEdges }"
            class="jwx-widget-editor-settings__swatches-scroller"
            :previousLabel="t('widgets.settings.previousColors')"
            :nextLabel="t('widgets.settings.moreColors')"
          >
            <div :ref="scrollerRef" class="jwx-widget-editor-settings__swatches" :data-fade="fadeEdges" role="group" :aria-label="t('widgets.settings.colorPresets')">
              <template v-if="customColors.length">
                <button
                  v-for="custom of customColors"
                  :key="custom"
                  @click="handleSwatchSelect(custom)"
                  type="button"
                  class="jwx-widget-editor-settings__swatch"
                  :style="{ '--jwx-widget-editor-settings-color': custom }"
                  :aria-pressed="isSelectedColor(custom)"
                  :aria-label="t('widgets.settings.customColor', { color: custom })"
                ></button>
                <span class="jwx-widget-editor-settings__swatch-divider" aria-hidden="true"></span>
              </template>
              <button
                v-for="preset of presetColors"
                :key="preset.key"
                @click="handleSwatchSelect(preset.color)"
                type="button"
                class="jwx-widget-editor-settings__swatch"
                :style="{ '--jwx-widget-editor-settings-color': preset.color }"
                :aria-pressed="isSelectedColor(preset.color)"
                :aria-label="t(`widgets.colors.${preset.key}`)"
              ></button>
            </div>
          </ScrollAffordance>
        </div>
      </div>

      <div class="jwx-widget-editor-settings__column">
        <div class="jwx-widget-editor-settings__group" role="group" :aria-labelledby="`${idPrefix}-units`">
          <h3 :id="`${idPrefix}-units`" class="jwx-widget-editor-settings__heading">{{ t('widgets.settings.units') }}</h3>
          <div v-if="definition.units.includes('wind')" class="jwx-widget-editor-settings__unit">
            <span class="jwx-widget-editor-settings__unit-label" aria-hidden="true">{{ t('widgets.settings.wind') }}</span>
            <SegmentedControl
              :key="`${widgetId}-wind`"
              v-model="unitsWind"
              @segmentChanged="handleUnitsWindChange"
              :label="t('siteSettings.windSpeed')"
              size="small"
              shape="pill"
            >
              <SegmentedControlButton v-for="(label, key) of SPEED_UNITS" :key="key" :value="key">{{ label }}</SegmentedControlButton>
            </SegmentedControl>
          </div>
          <div v-if="definition.units.includes('temp')" class="jwx-widget-editor-settings__unit">
            <span class="jwx-widget-editor-settings__unit-label" aria-hidden="true">{{ t('widgets.settings.temperature') }}</span>
            <SegmentedControl
              :key="`${widgetId}-temp`"
              v-model="unitsTemp"
              @segmentChanged="handleUnitsTempChange"
              :label="t('siteSettings.temperature')"
              size="small"
              shape="pill"
            >
              <SegmentedControlButton v-for="(label, key) of TEMP_UNITS" :key="key" :value="key">{{ label }}</SegmentedControlButton>
            </SegmentedControl>
          </div>
        </div>

        <div v-if="sizeLimits" class="jwx-widget-editor-settings__group" role="group" :aria-labelledby="`${idPrefix}-size`">
          <h3 :id="`${idPrefix}-size`" class="jwx-widget-editor-settings__heading">{{ t('widgets.settings.size') }}</h3>
          <div class="jwx-widget-editor-settings__fields">
            <div class="jwx-widget-editor-settings__field">
              <label :for="`${idPrefix}-width`" class="jwx-widget-editor-settings__label">{{ t('widgets.settings.width') }}</label>
              <span class="jwx-widget-editor-settings__number">
                <input
                  :id="`${idPrefix}-width`"
                  v-model.number="widthDraft"
                  @change="handleWidthChange"
                  type="number"
                  inputmode="numeric"
                  class="jwx-widget-editor-settings__number-input"
                  :min="sizeLimits.minWidth"
                  :max="sizeLimits.maxWidth"
                  :aria-describedby="`${idPrefix}-width-range`"
                >
                <span class="jwx-widget-editor-settings__number-suffix" aria-hidden="true">px</span>
              </span>
              <span :id="`${idPrefix}-width-range`" class="jwx-widget-editor-settings__hint">
                {{ t('widgets.settings.range', { min: sizeLimits.minWidth, max: sizeLimits.maxWidth }) }}
              </span>
            </div>
            <div class="jwx-widget-editor-settings__field">
              <label :for="`${idPrefix}-height`" class="jwx-widget-editor-settings__label">{{ t('widgets.settings.height') }}</label>
              <span class="jwx-widget-editor-settings__number">
                <input
                  :id="`${idPrefix}-height`"
                  v-model.number="heightDraft"
                  @change="handleHeightChange"
                  type="number"
                  inputmode="numeric"
                  class="jwx-widget-editor-settings__number-input"
                  :min="sizeLimits.minHeight"
                  :max="sizeLimits.maxHeight"
                  :aria-describedby="`${idPrefix}-height-range`"
                >
                <span class="jwx-widget-editor-settings__number-suffix" aria-hidden="true">px</span>
              </span>
              <span :id="`${idPrefix}-height-range`" class="jwx-widget-editor-settings__hint">
                {{ t('widgets.settings.range', { min: sizeLimits.minHeight, max: sizeLimits.maxHeight }) }}
              </span>
            </div>
          </div>
        </div>

        <button
          @click="handleReset"
          type="button"
          class="jwx-button-control jwx-widget-editor-settings__reset"
          data-surface="0"
          data-shape="pill"
          :disabled="!widgetStore.isWidgetCustomized(widgetId)"
        >
          <Icon name="rotate-right" class="jwx-button-control__icon" />
          {{ t('widgets.settings.resetToDefaults') }}
        </button>
      </div>
    </div>

    <section class="jwx-widget-editor-settings__embed" :aria-labelledby="`${idPrefix}-embed`">
      <h3 :id="`${idPrefix}-embed`" class="jwx-widget-editor-settings__embed-title">{{ t('widgets.settings.embedCode') }}</h3>
      <textarea
        :value="embed.code"
        readonly
        class="jwx-widget-editor-settings__code"
        :aria-labelledby="`${idPrefix}-embed`"
        @focus="$event.target.select()"
      ></textarea>
      <CopyContent @copied="handleCopied">
        <template #default="{ copy }">
          <button
            @click="copy(embed.code)"
            type="button"
            class="jwx-button-control jwx-widget-editor-settings__copy"
            data-shape="pill"
            data-selected="true"
          >
            <Icon name="clipboard" class="jwx-button-control__icon" />
            {{ t('widgets.copyEmbed') }}
          </button>
        </template>
      </CopyContent>
    </section>
    <p v-if="widgetStore.showMembershipNote" class="jwx-widget-editor-settings__note">{{ t('widgets.settings.membershipNote') }}</p>
    <p class="jwx-widget-editor-settings__note">{{ t('widgets.settings.builderNote') }}</p>
  </section>
</template>
